import type { Me } from '@/api/types';
import { useMe } from './useSession';

export type ModuleKey = 'training' | 'nutrition' | 'health' | 'finance' | 'ai';

/** A requirement to show a navigation item, route or action. */
export interface AccessRule {
  module?: ModuleKey;
  feature?: string;
  /** Any of these permissions (OR). */
  anyPermission?: string[];
}

export function hasModule(me: Me, module: ModuleKey) {
  return me.entitlements.modules.includes(module);
}

/** The space offers the module to someone: included, or personal (each person
 * turns it on). Staff read a client's data in it, with the client's consent. */
export function offersModule(me: Me, module: ModuleKey) {
  return hasModule(me, module) || me.entitlements.personal.includes(module);
}

/** Staff open a personal module they did not turn on, to read their clients' data. */
export function readsClientsIn(me: Me, module: ModuleKey) {
  return me.entitlements.personal.includes(module) && hasPermission(me, `${module}.clients.read`);
}

export function hasFeature(me: Me, feature: string) {
  return me.entitlements.features.includes(feature);
}

export function hasPermission(me: Me, permission: string) {
  return me.permissions.includes(permission);
}

export function allows(me: Me, rule: AccessRule | undefined): boolean {
  if (!rule) return true;
  if (rule.module && !hasModule(me, rule.module) && !readsClientsIn(me, rule.module)) return false;
  if (rule.feature && !hasFeature(me, rule.feature)) return false;
  if (rule.anyPermission && !rule.anyPermission.some((p) => hasPermission(me, p))) return false;
  return true;
}

/** `const can = useAccess(); can({ module: 'training', anyPermission: [...] })` */
export function useAccess() {
  const me = useMe();
  return (rule: AccessRule | undefined) => allows(me, rule);
}

/** Staff (professionals/admins) vs. clients: drives which home screen to show. */
export function isStaff(me: Me) {
  return me.roles.includes('professional') || me.roles.includes('tenant_admin');
}
